/**
 * Distributed Game State Manager for P2P Poker
 * 
 * This module handles the distributed management of poker game state across peers,
 * replacing centralized server logic with consensus-based distributed state.
 * 
 * Key Features:
 * - Distributed consensus on game actions
 * - State synchronization across all peers
 * - Byzantine fault tolerance for cheating prevention
 * - Deterministic state transitions
 * - Conflict resolution and rollback mechanisms
 */

import { ByzantineConsensus } from './ByzantineConsensus';
import { MentalPoker } from '../../../shared/src/utils/mentalPoker';

// Distributed game state interfaces
interface DistributedPlayer {
  id: string;
  name: string;
  chips: number;
  currentBet: number;
  holeCards: string[];  // Encrypted for other players
  hasActed: boolean;
  isFolded: boolean;
  isConnected: boolean;
  isActive: boolean;
  lastActionTime: number;
  // Investment tracking (from v4.x)
  totalBuyIns: number;
  rebuyCount: number;
  netScore: number;
}

interface DistributedGameState {
  gameId: string;
  stateVersion: number;  // For conflict resolution
  stateHash: string;     // For integrity verification
  currentPlayerTurn: string;
  pot: number;
  currentBet: number;
  minRaise: number;
  dealerPosition: number;
  smallBlind: number;
  bigBlind: number;
  communityCards: string[];
  encryptedDeck: string[];  // Mental Poker encrypted deck
  round: 'preflop' | 'flop' | 'turn' | 'river' | 'showdown';
  bettingRoundComplete: boolean;
  playersToAct: string[];
  turnStartTime: number;
  actionTimeoutMs: number;
  // Consensus tracking
  lastActionConsensus: string;
  pendingActions: GameAction[];
}

interface GameAction {
  actionId: string;
  playerId: string;
  actionType: 'fold' | 'check' | 'call' | 'raise' | 'all-in';
  amount?: number;
  timestamp: number;
  signature: string;  // Cryptographic signature
  nonce: string;
  // Consensus metadata
  votes: { [peerId: string]: boolean };
  consensusReached: boolean;
  applied: boolean;
}

interface StateTransition {
  fromState: string;  // Previous state hash
  toState: string;    // New state hash
  action: GameAction;
  stateVersion: number;
  timestamp: number;
}

/**
 * Distributed Game State Manager
 * Orchestrates P2P poker game state across multiple peers
 */
export class DistributedGameState {
  private static instance: DistributedGameState;
  
  // Core game state
  private gameState: DistributedGameState | null = null;
  private players: Map<string, DistributedPlayer> = new Map();
  private myPlayerId: string = '';
  private roomId: string = '';
  
  // Consensus and synchronization
  private byzantineConsensus: ByzantineConsensus;
  private mentalPoker: MentalPoker;
  private stateHistory: StateTransition[] = [];
  private pendingConsensus: Map<string, GameAction> = new Map();
  
  // State management
  private stateUpdateHandlers: Array<(state: DistributedGameState) => void> = [];
  private actionHandlers: Array<(action: GameAction) => void> = [];
  private errorHandlers: Array<(error: string) => void> = [];
  
  // Synchronization
  private stateSyncTimer: NodeJS.Timeout | null = null;
  private consensusTimeout: NodeJS.Timeout | null = null;
  
  private constructor() {
    this.byzantineConsensus = ByzantineConsensus.getInstance();
    // Initialize with empty arrays - will be properly initialized when game starts
    this.mentalPoker = new MentalPoker('temp', []);
    this.setupEventHandlers();
  }
  
  static getInstance(): DistributedGameState {
    if (!DistributedGameState.instance) {
      DistributedGameState.instance = new DistributedGameState();
    }
    return DistributedGameState.instance;
  }
  
  /**
   * Initialize distributed game state for a new game
   */
  async initializeGame(
    gameId: string,
    roomId: string,
    playerId: string,
    players: DistributedPlayer[],
    gameConfig: { smallBlind: number; bigBlind: number }
  ): Promise<void> {
    console.log('🎮 Initializing distributed game state:', gameId);
    
    this.roomId = roomId;
    this.myPlayerId = playerId;
    
    // Initialize players map
    this.players.clear();
    players.forEach(player => {
      this.players.set(player.id, { ...player });
    });
    
    // Create initial game state
    this.gameState = {
      gameId,
      stateVersion: 1,
      stateHash: '',
      currentPlayerTurn: '',
      pot: 0,
      currentBet: 0,
      minRaise: gameConfig.bigBlind,
      dealerPosition: 0,
      smallBlind: gameConfig.smallBlind,
      bigBlind: gameConfig.bigBlind,
      communityCards: [],
      encryptedDeck: [],
      round: 'preflop',
      bettingRoundComplete: false,
      playersToAct: [],
      turnStartTime: Date.now(),
      actionTimeoutMs: 60000,
      lastActionConsensus: '',
      pendingActions: []
    };
    
    // Generate state hash
    this.gameState.stateHash = await this.generateStateHash(this.gameState);
    
    // Initialize Mental Poker for this game
    await this.initializeMentalPoker(players.map(p => p.id));
    
    // Start state synchronization
    this.startStateSynchronization();
    
    console.log('✅ Distributed game state initialized');
    
    // Automatically start the first poker round
    await this.startPokerRound();
  }
  
  /**
   * Start a new poker round (deal cards, set blinds, begin betting)
   */
  async startPokerRound(): Promise<void> {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    
    console.log('🃏 Starting new poker round...');
    
    // Reset round state
    this.gameState.round = 'preflop';
    this.gameState.communityCards = [];
    this.gameState.pot = 0;
    this.gameState.currentBet = this.gameState.bigBlind;
    this.gameState.bettingRoundComplete = false;
    
    // Reset player states for new round
    this.players.forEach(player => {
      player.currentBet = 0;
      player.hasActed = false;
      player.isFolded = false;
      player.holeCards = [];
    });
    
    // Set blinds
    await this.setBlinds();
    
    // Deal hole cards using Mental Poker
    await this.dealHoleCards();
    
    // Determine first player to act (after big blind)
    this.setFirstPlayerToAct();
    
    // Update game state version and notify
    this.gameState.stateVersion++;
    this.gameState.stateHash = await this.calculateStateHash();
    
    console.log('✅ Poker round started');
    
    // Emit state update
    this.emitStateUpdate(this.gameState);
  }
  
  /**
   * Set blinds for the current round
   */
  private async setBlinds(): Promise<void> {
    const activePlayers = this.players.filter(p => p.isActive);
    if (activePlayers.length < 2) return;
    
    // Find dealer, small blind, and big blind positions
    const dealerIndex = this.gameState!.dealerPosition % activePlayers.length;
    const smallBlindIndex = (dealerIndex + 1) % activePlayers.length;
    const bigBlindIndex = (dealerIndex + 2) % activePlayers.length;
    
    const smallBlindPlayer = activePlayers[smallBlindIndex];
    const bigBlindPlayer = activePlayers[bigBlindIndex];
    
    // Set small blind
    const smallBlindAmount = Math.min(smallBlindPlayer.chips, this.gameState!.smallBlind);
    smallBlindPlayer.chips -= smallBlindAmount;
    smallBlindPlayer.currentBet = smallBlindAmount;
    this.gameState!.pot += smallBlindAmount;
    
    // Set big blind
    const bigBlindAmount = Math.min(bigBlindPlayer.chips, this.gameState!.bigBlind);
    bigBlindPlayer.chips -= bigBlindAmount;
    bigBlindPlayer.currentBet = bigBlindAmount;
    this.gameState!.pot += bigBlindAmount;
    
    console.log(`💰 Blinds set: ${smallBlindPlayer.name} (${smallBlindAmount}), ${bigBlindPlayer.name} (${bigBlindAmount})`);
  }
  
  /**
   * Deal hole cards to all players using Mental Poker
   */
  private async dealHoleCards(): Promise<void> {
    console.log('🃏 Dealing hole cards...');
    
    // For now, simulate card dealing (Mental Poker integration pending)
    const deck = this.createShuffledDeck();
    let cardIndex = 0;
    
    for (const player of this.players.filter(p => p.isActive)) {
      player.holeCards = [
        deck[cardIndex++],
        deck[cardIndex++]
      ];
    }
    
    console.log('✅ Hole cards dealt to all players');
  }
  
  /**
   * Set the first player to act
   */
  private setFirstPlayerToAct(): void {
    const activePlayers = this.players.filter(p => p.isActive);
    if (activePlayers.length < 2) return;
    
    // First to act is typically the player after big blind (UTG)
    const dealerIndex = this.gameState!.dealerPosition % activePlayers.length;
    const firstToActIndex = (dealerIndex + 3) % activePlayers.length;
    
    this.gameState!.currentPlayerTurn = activePlayers[firstToActIndex].id;
    this.gameState!.turnStartTime = Date.now();
    
    console.log(`🎯 First to act: ${activePlayers[firstToActIndex].name}`);
  }
  
  /**
   * Create a shuffled deck of cards
   */
  private createShuffledDeck(): string[] {
    const suits = ['h', 'd', 'c', 's']; // hearts, diamonds, clubs, spades
    const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
    
    const deck: string[] = [];
    for (const suit of suits) {
      for (const rank of ranks) {
        deck.push(rank + suit);
      }
    }
    
    // Simple shuffle (in production, this would use Mental Poker)
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    
    return deck;
  }
  
  /**
   * Process a player action through distributed consensus
   */
  async processPlayerAction(
    playerId: string,
    actionType: 'fold' | 'check' | 'call' | 'raise' | 'all-in',
    amount?: number
  ): Promise<boolean> {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    
    // Validate action
    if (!this.validateAction(playerId, actionType, amount)) {
      console.error('❌ Invalid action:', { playerId, actionType, amount });
      return false;
    }
    
    console.log(`🎯 Processing P2P action: ${playerId} ${actionType} ${amount || ''}`);
    
    // Create signed action
    const action: GameAction = {
      actionId: this.generateActionId(),
      playerId,
      actionType,
      amount,
      timestamp: Date.now(),
      signature: await this.signAction({ playerId, actionType, amount }),
      nonce: this.generateNonce(),
      votes: {},
      consensusReached: false,
      applied: false
    };
    
    // Submit to Byzantine consensus
    try {
      const consensusResult = await this.byzantineConsensus.proposeAction(
        action.actionId,
        action
      );
      
      if (consensusResult.approved) {
        // Apply the action to game state
        await this.applyActionToState(action);
        return true;
      } else {
        console.warn('❌ Action rejected by consensus:', consensusResult.reason);
        return false;
      }
    } catch (error) {
      console.error('❌ Consensus error:', error);
      this.emitError('Consensus failed: ' + error);
      return false;
    }
  }
  
  /**
   * Apply a consensus-approved action to the game state
   */
  private async applyActionToState(action: GameAction): Promise<void> {
    if (!this.gameState) return;
    
    const player = this.players.get(action.playerId);
    if (!player) return;
    
    console.log(`✅ Applying action: ${player.name} ${action.actionType} ${action.amount || ''}`);
    
    // Create new state version
    const newState = { ...this.gameState };
    newState.stateVersion += 1;
    
    // Process the action based on type
    switch (action.actionType) {
      case 'fold':
        player.isFolded = true;
        player.hasActed = true;
        break;
        
      case 'check':
        if (player.currentBet < newState.currentBet) {
          throw new Error('Cannot check - must call or fold');
        }
        player.hasActed = true;
        break;
        
      case 'call':
        const callAmount = Math.min(
          newState.currentBet - player.currentBet,
          player.chips
        );
        player.chips -= callAmount;
        player.currentBet += callAmount;
        newState.pot += callAmount;
        player.hasActed = true;
        break;
        
      case 'raise':
        if (!action.amount || action.amount < newState.currentBet + newState.minRaise) {
          throw new Error('Invalid raise amount');
        }
        const raiseAmount = Math.min(action.amount - player.currentBet, player.chips);
        player.chips -= raiseAmount;
        player.currentBet += raiseAmount;
        newState.pot += raiseAmount;
        newState.currentBet = player.currentBet;
        player.hasActed = true;
        
        // Reset other players' hasActed status
        this.players.forEach(p => {
          if (p.id !== action.playerId && !p.isFolded) {
            p.hasActed = false;
          }
        });
        break;
        
      case 'all-in':
        const allInAmount = player.chips;
        player.chips = 0;
        player.currentBet += allInAmount;
        newState.pot += allInAmount;
        
        if (player.currentBet > newState.currentBet) {
          newState.currentBet = player.currentBet;
          // Reset other players' hasActed status
          this.players.forEach(p => {
            if (p.id !== action.playerId && !p.isFolded) {
              p.hasActed = false;
            }
          });
        }
        player.hasActed = true;
        break;
    }
    
    // Update turn and check for round completion
    await this.updateGameFlow(newState);
    
    // Generate new state hash
    newState.stateHash = await this.generateStateHash(newState);
    
    // Save state transition
    this.stateHistory.push({
      fromState: this.gameState.stateHash,
      toState: newState.stateHash,
      action,
      stateVersion: newState.stateVersion,
      timestamp: Date.now()
    });
    
    // Update game state
    this.gameState = newState;
    
    // Notify handlers
    this.emitStateUpdate(this.gameState);
    this.emitActionProcessed(action);
    
    console.log(`🔄 State updated to version ${newState.stateVersion}`);
  }
  
  /**
   * Update game flow (turn management, round progression)
   */
  private async updateGameFlow(state: DistributedGameState): Promise<void> {
    // Check if only one player remains active
    const activePlayers = Array.from(this.players.values()).filter(
      p => !p.isFolded && p.isConnected && p.chips > 0
    );
    
    if (activePlayers.length === 1) {
      // Game ends - winner takes pot
      const winner = activePlayers[0];
      winner.chips += state.pot;
      state.pot = 0;
      state.round = 'showdown';
      console.log(`🏆 Game ends: ${winner.name} wins ${state.pot}`);
      return;
    }
    
    // Check if betting round is complete
    if (this.isBettingRoundComplete()) {
      await this.advanceToNextRound(state);
    } else {
      // Set next player to act
      this.setNextPlayerToAct(state);
    }
  }
  
  /**
   * Check if current betting round is complete
   */
  private isBettingRoundComplete(): boolean {
    const activePlayers = Array.from(this.players.values()).filter(
      p => !p.isFolded && p.isConnected
    );
    
    // All active players have acted and have matching bets (or are all-in)
    return activePlayers.every(p => 
      p.hasActed && (p.currentBet === this.gameState!.currentBet || p.chips === 0)
    );
  }
  
  /**
   * Advance to next betting round
   */
  private async advanceToNextRound(state: DistributedGameState): Promise<void> {
    console.log(`🔄 Advancing from ${state.round}`);
    
    // Reset players for new betting round
    this.players.forEach(p => {
      if (!p.isFolded && p.isConnected) {
        p.hasActed = false;
        p.currentBet = 0;
      }
    });
    
    state.currentBet = 0;
    
    // Progress through rounds
    switch (state.round) {
      case 'preflop':
        state.round = 'flop';
        await this.dealCommunityCards(3);
        break;
      case 'flop':
        state.round = 'turn';
        await this.dealCommunityCards(1);
        break;
      case 'turn':
        state.round = 'river';
        await this.dealCommunityCards(1);
        break;
      case 'river':
        state.round = 'showdown';
        await this.handleShowdown();
        return;
    }
    
    // Set first player to act in new round
    this.setNextPlayerToAct(state);
  }
  
  /**
   * Set the next player to act
   */
  private setNextPlayerToAct(state: DistributedGameState): void {
    const activePlayers = Array.from(this.players.values()).filter(
      p => !p.isFolded && p.isConnected && p.chips > 0
    );
    
    if (activePlayers.length === 0) return;
    
    // Find current player index
    const currentIndex = activePlayers.findIndex(p => p.id === state.currentPlayerTurn);
    
    // Get next player
    let nextIndex = (currentIndex + 1) % activePlayers.length;
    
    // Find next player who hasn't acted or needs to act
    while (activePlayers[nextIndex].hasActed && 
           activePlayers[nextIndex].currentBet === state.currentBet) {
      nextIndex = (nextIndex + 1) % activePlayers.length;
      if (nextIndex === currentIndex) break; // Avoid infinite loop
    }
    
    state.currentPlayerTurn = activePlayers[nextIndex].id;
    state.turnStartTime = Date.now();
    
    console.log(`👉 Next to act: ${activePlayers[nextIndex].name}`);
  }
  
  /**
   * Deal community cards using Mental Poker
   */
  private async dealCommunityCards(count: number): Promise<void> {
    // TODO: Implement Mental Poker card dealing
    // For now, use deterministic placeholder
    const cards = ['2♠', '7♥', '3♦', 'K♣', 'A♠'].slice(0, count);
    
    if (this.gameState) {
      this.gameState.communityCards.push(...cards);
      console.log(`🃏 Dealt ${count} community cards:`, cards);
    }
  }
  
  /**
   * Handle showdown with secure hand comparison
   */
  private async handleShowdown(): Promise<void> {
    console.log('🏆 Starting showdown...');
    
    // TODO: Implement secure multi-party hand comparison
    // For now, use simplified logic
    
    const activePlayers = Array.from(this.players.values()).filter(
      p => !p.isFolded && p.isConnected
    );
    
    if (activePlayers.length === 1) {
      // Only one player remaining
      const winner = activePlayers[0];
      winner.chips += this.gameState!.pot;
      console.log(`🏆 ${winner.name} wins ${this.gameState!.pot} (only remaining player)`);
    } else {
      // TODO: Implement proper hand evaluation and comparison
      // For now, first player wins
      const winner = activePlayers[0];
      winner.chips += this.gameState!.pot;
      console.log(`🏆 ${winner.name} wins ${this.gameState!.pot} (placeholder logic)`);
    }
    
    if (this.gameState) {
      this.gameState.pot = 0;
    }
  }
  
  /**
   * Mental Poker initialization
   */
  private async initializeMentalPoker(playerIds: string[]): Promise<void> {
    // TODO: Implement full Mental Poker initialization
    console.log('🔐 Initializing Mental Poker for players:', playerIds);
  }
  
  /**
   * Validate player action
   */
  private validateAction(
    playerId: string,
    actionType: string,
    amount?: number
  ): boolean {
    if (!this.gameState) return false;
    
    // Check if it's player's turn
    if (this.gameState.currentPlayerTurn !== playerId) {
      console.error('❌ Not player\'s turn');
      return false;
    }
    
    const player = this.players.get(playerId);
    if (!player || player.isFolded || !player.isConnected) {
      console.error('❌ Invalid player state');
      return false;
    }
    
    // Validate specific actions
    switch (actionType) {
      case 'check':
        return player.currentBet >= this.gameState.currentBet;
      case 'call':
        return this.gameState.currentBet > player.currentBet;
      case 'raise':
        return amount && amount >= this.gameState.currentBet + this.gameState.minRaise;
      case 'all-in':
        return player.chips > 0;
      case 'fold':
        return true;
      default:
        return false;
    }
  }
  
  /**
   * State synchronization and consensus
   */
  private setupEventHandlers(): void {
    // Handle Byzantine consensus results
    this.byzantineConsensus.setConsensusReachedHandler((actionId, payload) => {
      this.handleConsensusResult(actionId, payload);
    });
    
    // Handle Byzantine faults
    this.byzantineConsensus.setByzantineDetectedHandler((suspiciousNode, evidence) => {
      console.warn('🚨 Byzantine fault detected:', suspiciousNode);
      this.emitError(`Byzantine fault detected from ${suspiciousNode}`);
    });
  }
  
  private handleConsensusResult(actionId: string, payload: any): void {
    console.log('✅ Consensus reached for action:', actionId);
    
    const pendingAction = this.pendingConsensus.get(actionId);
    if (pendingAction) {
      pendingAction.consensusReached = true;
      this.pendingConsensus.delete(actionId);
    }
  }
  
  private startStateSynchronization(): void {
    // Periodic state sync with peers
    this.stateSyncTimer = setInterval(() => {
      this.syncStateWithPeers();
    }, 5000); // Every 5 seconds
  }
  
  private async syncStateWithPeers(): Promise<void> {
    // TODO: Implement state synchronization with peers
    console.log('🔄 Syncing state with peers...');
  }
  
  /**
   * Utility functions
   */
  private async generateStateHash(state: DistributedGameState): Promise<string> {
    const stateString = JSON.stringify({
      gameId: state.gameId,
      currentPlayerTurn: state.currentPlayerTurn,
      pot: state.pot,
      currentBet: state.currentBet,
      round: state.round,
      communityCards: state.communityCards,
      players: Array.from(this.players.entries()).map(([id, p]) => ({
        id,
        chips: p.chips,
        currentBet: p.currentBet,
        isFolded: p.isFolded,
        hasActed: p.hasActed
      }))
    });
    
    const encoder = new TextEncoder();
    const data = encoder.encode(stateString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  private generateActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private generateNonce(): string {
    const nonce = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(nonce).map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  private async signAction(action: any): Promise<string> {
    const actionData = JSON.stringify(action) + this.myPlayerId;
    const encoder = new TextEncoder();
    const data = encoder.encode(actionData);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
  
  /**
   * Event handlers
   */
  onStateUpdate(handler: (state: DistributedGameState) => void): void {
    this.stateUpdateHandlers.push(handler);
  }
  
  onActionProcessed(handler: (action: GameAction) => void): void {
    this.actionHandlers.push(handler);
  }
  
  onError(handler: (error: string) => void): void {
    this.errorHandlers.push(handler);
  }
  
  private emitStateUpdate(state: DistributedGameState): void {
    this.stateUpdateHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        console.error('Error in state update handler:', error);
      }
    });
  }
  
  private emitActionProcessed(action: GameAction): void {
    this.actionHandlers.forEach(handler => {
      try {
        handler(action);
      } catch (error) {
        console.error('Error in action handler:', error);
      }
    });
  }
  
  private emitError(error: string): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (error) {
        console.error('Error in error handler:', error);
      }
    });
  }
  
  /**
   * Getters
   */
  getGameState(): DistributedGameState | null {
    return this.gameState;
  }
  
  getPlayers(): DistributedPlayer[] {
    return Array.from(this.players.values());
  }
  
  getMyPlayer(): DistributedPlayer | null {
    return this.players.get(this.myPlayerId) || null;
  }
  
  /**
   * Cleanup
   */
  cleanup(): void {
    if (this.stateSyncTimer) {
      clearInterval(this.stateSyncTimer);
    }
    if (this.consensusTimeout) {
      clearTimeout(this.consensusTimeout);
    }
    
    this.stateUpdateHandlers = [];
    this.actionHandlers = [];
    this.errorHandlers = [];
  }
}